<script setup>
import { computed, ref } from 'vue'
import { useUsersStore } from '@/stores'
import RestaurantHeart from '@core/components/RestaurantHeart.vue'

// THE restaurant identity cluster — name, heart + count, group, Preferred —
// rendered identically on every restaurant row (shared card, Search
// dropdowns, request-dialog summary, AI-chat rows). This is a component,
// not a CSS convention, precisely so the surfaces cannot drift: there is
// one markup structure and one scale.
//
// The root uses display:contents, so the pieces participate directly in
// the consumer's own layout (flex column, grid cell) without adding a box.
// Geometry of the title line comes from the global .rm-title-heart /
// .heart-unit spec in styles.scss.

const props = defineProps({
  // The restaurant object for the heart (favorite/fans logic). Optional —
  // without it RestaurantHeart resolves the restaurant from the store by
  // name + market (the request-dialog rows use that path).
  restaurant: {
    type: Object,
    default: null
  },

  name: {
    type: String,
    required: true
  },

  market: {
    type: String,
    default: ''
  },

  group: {
    type: String,
    default: ''
  },

  // Show the city as the cluster's LAST line. Off by default and enabled only
  // on the RM AI rows: main search draws its own city line, and Discover is
  // already filtered by the city selector. The AI rows were the surface that
  // lacked it — four "Maple & Ash" locations rendered identically, so the
  // agent's "which location?" question could not be answered from the screen.
  showMarket: {
    type: Boolean,
    default: false
  },

  // A quiet note on the name line, after the heart and its count. RM AI's rows
  // pass "doesn't take inquiries via RM" for a restaurant that has opted out
  // (RM-384, the product agent 10/5). It rides the name line when there is
  // room and drops to the line below when there isn't (a long name on a phone).
  inlineNote: {
    type: String,
    default: ''
  },

  // Restaurant status — 'Preferred' renders the tappable word
  status: {
    type: String,
    default: ''
  },

  // Narrow contexts (Search dropdown, AI rows, dialogs) stay on the
  // mobile tier of the scale instead of growing at desktop widths
  compact: {
    type: Boolean,
    default: false
  },

  // Dense text lists (the AI-chat rows, RM-288): no photo to balance the
  // 20px name, so the name steps down to 17px while the meta lines stay on
  // the mobile tier. Kapil, 2026-09-15: "the names feel really big".
  dense: {
    type: Boolean,
    default: false
  },

  // The restaurant's own page shows the WHOLE name, wrapping, with the heart
  // following the last word (Kapil, 2026-09-23). Rows keep one line and an
  // ellipsis; on the profile page a long name ran off the card instead and
  // took the heart, its count and the share button with it.
  wrap: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['name-click'])

const usersStore = useUsersStore()

const isPreferred = computed(() => props.status === 'Preferred')

const heartRef = ref(null)

// Consumers drive the heart through the block (fans drawer, "+" favorite)
defineExpose({
  openFavoritedBy: () => heartRef.value?.openFavoritedBy(),
  toggleFavorite: () => heartRef.value?.toggleFavorite(),
})
</script>

<template>
  <div
    class="rtb"
    :class="{ 'rtb--compact': compact, 'rtb--dense': dense, 'rtb--wrap': wrap }"
  >
    <div
      class="rtb__title rm-title-heart"
      :class="{ 'rtb__title--noted': !!inlineNote }"
    >
      <!-- No .stop — the row's own click still fires under the name -->
      <span
        class="rtb__name"
        @click="emit('name-click')"
      >{{ name }}</span>
      <span
        class="heart-unit"
        @click.stop
      >
        <RestaurantHeart
          ref="heartRef"
          :restaurant-name="name"
          :restaurant-market="market"
          :restaurant="restaurant"
        />
      </span>
      <span
        v-if="inlineNote"
        class="rtb__inline-note"
      >- {{ inlineNote }}</span>
    </div>

    <!--
      Group — quiet subtitle; omitted entirely when the restaurant has no
      group, so Preferred sits directly beneath the name
    -->
    <p
      v-if="group"
      class="rtb__group"
    >
      {{ group }}
    </p>

    <!-- Preferred — plain tappable word (no icon), opens the For Restaurants modal -->
    <button
      v-if="isPreferred"
      class="rtb__preferred"
      aria-label="About Preferred restaurants"
      @click.stop="usersStore.isShowingRestaurantsInfoDialog = true"
    >
      Preferred
    </button>

    <!--
      City — the cluster's last line, beneath Preferred. Its own line rather
      than appended to the group: the group answers "whose is it", the city
      answers "which one", and merging them buries the city behind an ellipsis
      on narrow rows exactly when it matters most
    -->
    <p
      v-if="showMarket && market"
      class="rtb__market"
    >
      {{ market }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.rtb {
  // The block adds no box of its own — its lines lay out in the
  // consumer's flex/grid. (display:contents elements still work as
  // ancestors for the selectors below.)
  display: contents;
}

.rtb__title {
  min-width: 0;
  margin-bottom: 4px;
}

// With a note the title may take a second line: the note wraps whole, under the name
.rtb__title--noted {
  flex-wrap: wrap;
  row-gap: 2px;
}

// Inline note — the metadata scale and secondary ink: a fact beside the name, never a warning
.rtb__inline-note {
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-secondary);
  white-space: nowrap;
}

// Restaurant name — the serif (Playfair 500), brand ink, prototype scale
.rtb__name {
  min-width: 0;
  font-family: var(--ds-font-heading);
  font-size: 20px;
  font-weight: var(--ds-font-medium);
  line-height: 1.2;
  color: var(--ds-brand-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

// Dense list variant — one step down the scale, same face and ink
.rtb--dense .rtb__name {
  font-size: 17px;
}

// Wrap variant — the name flows onto as many lines as it needs and the heart
// unit rides after its last word, never alone on a line of its own. The title
// becomes a text block instead of a flex row so the heart can sit inline.
.rtb--wrap .rtb__title {
  display: block;
}

.rtb--wrap .rtb__name {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: break-word;
}

.rtb--wrap .heart-unit {
  display: inline-flex;
  margin-left: 8px;
  vertical-align: middle;
  white-space: nowrap;
}

.rtb__group {
  margin: 0;
  min-width: 0;
  font-family: var(--ds-font-body);
  font-size: 12.5px; // mobile tier — exact 1.6× step under the 20px name
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rtb__market {
  margin: 2px 0 0;
  min-width: 0;
  font-family: var(--ds-font-body);
  font-size: 12.5px; // mobile tier — matches the group line
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rtb__preferred {
  display: block;
  background: transparent;
  border: none;
  padding: 0;
  margin-top: 2px;
  width: fit-content;
  font-family: var(--ds-font-body);
  font-size: 12.5px; // mobile tier, matching the group line
  font-weight: 500;
  line-height: 1.3;
  color: var(--ds-brand-primary-dark);
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

}

// Desktop tier — full-width surfaces only; compact contexts keep the
// mobile scale
@media (min-width: 960px) {
  .rtb:not(.rtb--compact) {
    .rtb__name {
      font-size: 23px;
    }

    .rtb__group,
    .rtb__market {
      font-size: 14px;
    }

    .rtb__preferred {
      font-size: 13.5px;
    }
  }
}
</style>
